import { html, nothing } from "lit";
import { query } from "lit/decorators.js";
import { BaseComponent } from "../../BaseComponent.js";
import "../../atom/Icon/index.js";
import { getButtonClasses, getIconClasses, getArrowIconClasses, getXIconClasses } from "./utils.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { testingAttr } from "../../../directives/testingAttr.js";
import { TestingAttrs } from "./filterButton.selectors.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
const sizes = ["medium", "small", "smaller"];
let DsfFilterButton = class extends BaseComponent {
  constructor() {
    super(...arguments);
    this.disabled = false;
    this.dsf_options = [];
    this._group_position = "ungrouped";
    this.dsf_open = false;
    this.dsf_multiselect = false;
    this.dsf_size = "medium";
  }
  get textLabel() {
    return `${this.dsf_prefix ? `${this.dsf_prefix} ` : ""}${this.dsf_options.length ? this.dsf_options.join(", ") : ""}`;
  }
  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));
    this.resizeObserver.observe(this);
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    (_a = this.resizeObserver) == null ? void 0 : _a.disconnect();
    this.resizeObserver = void 0;
  }
  async updated(changedProps) {
    super.updated(changedProps);
    await this.updateComplete;
    this.updateHiddenCount();
  }
  updateHiddenCount() {
    if (!this.dsf_multiselect || !this.dsf_options.length) {
      return;
    }
    this.hiddenCountLabel.style.display = "none";
    const labelOverflow = this.labelContainer.offsetWidth - this.label.scrollWidth;
    if (labelOverflow < 0) {
      this.hiddenCountLabel.innerText = `+0`;
      this.hiddenCountLabel.style.display = "";
      const averageCharWidth = this.label.scrollWidth / this.textLabel.length;
      const visibleCharCount = this.label.offsetWidth / averageCharWidth;
      const visibleText = this.textLabel.slice(
        this.dsf_prefix ? this.dsf_prefix.length : 0,
        visibleCharCount - 3 || 0
      );
      const visibleOptionsCount = visibleText.split(", ").length;
      const hiddenOptionsCount = this.dsf_options.length - visibleOptionsCount;
      if (hiddenOptionsCount > 0) {
        this.hiddenCountLabel.innerText = `+${hiddenOptionsCount}`;
        this.hiddenCountLabel.style.display = "";
      } else {
        this.hiddenCountLabel.style.display = "none";
      }
    }
  }
  focus() {
    this.rootElement.focus();
  }
  handleResize() {
    this.updateHiddenCount();
  }
  handleClearClick() {
    if (this.disabled) {
      return;
    }
    this.emit("clear-click");
  }
  handleClearMouseDown(event) {
    event.stopPropagation();
  }
  renderSelectContent() {
    if (!this.dsf_options.length) {
      return html`<span class="flex-auto truncate">${this.dsf_placeholder}</span>`;
    }
    const textLabel = html`<span id="label" class="flex-auto truncate" title=${this.textLabel}>
      ${this.textLabel}
    </span>`;
    if (!this.dsf_multiselect) {
      return textLabel;
    }
    return html`
      <div id="labelContainer" class="flex flex-auto items-center gap-4 overflow-hidden">
        ${textLabel}
        <div id="hiddenCountLabel" title=${this.textLabel} class="shrink-0"></div>
      </div>
    `;
  }
  render() {
    const buttonClasses = getButtonClasses(this, this._group_position);
    const iconClasses = getIconClasses(this);
    const arrowIconClasses = getArrowIconClasses(this);
    const xIconClasses = getXIconClasses(this);
    const selectContent = this.renderSelectContent();
    const icon = this.dsf_options.length ? html`<dsf-icon
          dsf_name="X"
          class=${xIconClasses}
          @click=${this.handleClearClick}
          @mousedown=${this.handleClearMouseDown}
          ${testingAttr(TestingAttrs.clearButton)}
        ></dsf-icon>` : html`<dsf-icon dsf_name="ChevronDown" class=${arrowIconClasses}></dsf-icon>`;
    return html`<div
      id="root"
      class=${buttonClasses}
      tabindex=${this.disabled ? "-1" : "0"}
      part="filter-button"
    >
      ${this.dsf_icon ? html`<dsf-icon dsf_name=${this.dsf_icon} class=${iconClasses}></dsf-icon>` : nothing}
      ${selectContent}
      <slot class="flex flex-auto whitespace-nowrap"></slot>
      ${icon}
    </div>`;
  }
};
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfFilterButton.prototype, "disabled", 2);
__decorateClass([
  prop({
    type: Array,
    converter: {
      fromAttribute: (attr) => {
        return attr.split(",").map((v) => v.trim()).filter((v) => !!v);
      }
    }
  })
], DsfFilterButton.prototype, "dsf_options", 2);
__decorateClass([
  prop({ type: String })
], DsfFilterButton.prototype, "_group_position", 2);
__decorateClass([
  prop({ type: String })
], DsfFilterButton.prototype, "dsf_placeholder", 2);
__decorateClass([
  prop({ type: String })
], DsfFilterButton.prototype, "dsf_prefix", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfFilterButton.prototype, "dsf_open", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfFilterButton.prototype, "dsf_multiselect", 2);
__decorateClass([
  prop({ type: String, reflect: true, validator: (value) => sizes.includes(value) })
], DsfFilterButton.prototype, "dsf_size", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfFilterButton.prototype, "dsf_icon", 2);
__decorateClass([
  query("#root")
], DsfFilterButton.prototype, "rootElement", 2);
__decorateClass([
  query("#labelContainer")
], DsfFilterButton.prototype, "labelContainer", 2);
__decorateClass([
  query("#label")
], DsfFilterButton.prototype, "label", 2);
__decorateClass([
  query("#hiddenCountLabel")
], DsfFilterButton.prototype, "hiddenCountLabel", 2);
DsfFilterButton = __decorateClass([
  defineElement("dsf-filter-button")
], DsfFilterButton);
export {
  DsfFilterButton,
  sizes
};
